import { AfterViewInit, Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { NgForm, NgModel } from "@angular/forms";
import { CardNumberHostComponent } from "@app/components/card/card-number-host.component";
import { Def } from "@card/common/definitions";
import { MonthYearData } from "@app/components/util/month-year-selects.component";
import { MatDialog } from "@angular/material/dialog";
import { CardCcvHelperDialog } from "@app/pages/guestpayment/guestpaymentcard/dialogs/card-ccv-helper.dialog";
import { WindowToken } from "@app/util/window.token";
import { debounce } from "@app/support/util/debounce.function";
import { AddressFormData } from "@app/components/card/card-address.component";
import { AppDataService } from "@app/services/appData.service";

export type CardDetailAddData = {
    makeDefault: boolean;
    nameOnCard: string;
    expMonth: number;
    expYear: number;
    secCode: string;
    nickName?: string;
}

@Component({
    selector: 'app-card-detail-add',
    template: require('./card-detail-add.component.html')
})
export class CardDetailAddComponent implements OnInit, AfterViewInit{

    @Input() props: any;
    @Input() addressDisabled: boolean;
    @Input() cardDisabled: boolean;
    @Input() showNickName: boolean;
    @Input() showDefault = true;

    @Output() onClearCard = new EventEmitter<void>();
    @Output() onFormComplete = new EventEmitter<boolean>();

    @ViewChild('cardDetailAddForm', { static: true }) cardDetailAddForm: NgForm;
    @ViewChild(CardNumberHostComponent) cardNumberHostComponent: CardNumberHostComponent;

    private cardUrl: string
    private cardInputHeight: string = '80px'
    private isInitialized = false
    private cardNumberEditor: Def.Editor = null

    cardConfig: Def.Config;

    formValues = {
        nameOnCard: "",
        defaultPaymentMethod: false,
        cardMonth: -1,
        cardYear: -1,
        securityCode: "",
        nickName: ""
    }

    formErrors = {
        nameOnCard: false,
        securityCode: false,
        monthYear: false,
        nickName: false,
        card: false,
    }

    constructor(@Inject(AppDataService) private appDataService: AppDataService,
                @Inject(WindowToken) private $window: Window,
                @Inject(MatDialog) private dialog: MatDialog) {}

    ngAfterViewInit(): void {
        this.setup();
    }

    ngOnInit(): void {
        this.cardConfig = {
            hoapass_url: this.appDataService.appConfig.get_APP_ENV().HOAPASS_API_URL,
            digital_payment_url: this.appDataService.appConfig.get_APP_ENV().DIGITAL_PAYMENT_URL,
            log: false,
            debug: false,
            show_debug_state: false,
        };
        this.cardUrl = this.cardConfig.digital_payment_url + "/card/"
    }

    getFormData(): CardDetailAddData {
        let returnObj: CardDetailAddData = {
            makeDefault: this.formValues.defaultPaymentMethod,
            nameOnCard: this.formValues.nameOnCard,
            expMonth: this.formValues.cardMonth,
            expYear: this.formValues.cardYear,
            secCode: this.formValues.securityCode,
        }

        if (this.showNickName) {
            returnObj.nickName = this.formValues.nickName;
        }

        return returnObj;
    }

    validateFormAndEmit() {
        if(this.allRequiredFields() && this.noErrors()) {
            this.onFormComplete.emit(true);
        } else {
            this.onFormComplete.emit(false);
        }
    }

    allRequiredFields(): boolean {
        let returnBool = false;

        if(this.formValues.nameOnCard !== "" &&
            this.formValues.securityCode !== "" &&
            this.formValues.cardMonth > -1 &&
            this.formValues.cardYear > -1 ) {
            returnBool = true;
        }

        if(returnBool && this.showNickName){
            return this.formValues.nickName !== "";
        } else {
            return returnBool;
        }
    }

    noErrors(): boolean {
        return !this.formErrors.nameOnCard &&
           !this.formErrors.card &&
           !this.formErrors.monthYear &&
           !this.formErrors.securityCode &&
           !this.formErrors.nickName
    }

    private setup() {
        this.isInitialized = false

        if (this.isInitialized) return
        this.isInitialized = true

        const setEditor = (editor: Def.Editor) => {
            this.cardNumberEditor = editor
        }

        this.cardNumberHostComponent.editorCreator.createNew(
            { ...this.cardConfig, debug: true }, {
                onTouched: (isTouched) => {
                    this.cardNumberHostComponent.updateTouched(isTouched);
                },
                onValidated: (isValid, errors = []) => {
                    this.cardNumberHostComponent.handleResults(isValid, errors);
                    this.formErrors.card = !isValid;
                    this.validateFormAndEmit();
                }
            }
        ).then(setEditor)
    }

    monthYearModelChange(data: MonthYearData) {
        this.formErrors.monthYear = !data.isValid;
        this.formValues.cardYear = data.yearValue;
        this.formValues.cardMonth = data.monthValue;
        this.validateFormAndEmit();
    }

    showSecurityCodeHelp() {
        this.dialog.open(CardCcvHelperDialog, {});
    }

    onBlur(modelControl: NgModel) {
        if(!modelControl.touched || modelControl.pristine) {
            return;
        }

        switch (modelControl.name) {
            case "nameOnCard":
                this.formErrors.nameOnCard = !(modelControl.status === "VALID")
                break;
            case "securityCode":
                this.formErrors.securityCode = !(modelControl.value.length === 3 || modelControl.value.length === 4);
                break;
            case "cardNickname":
                this.formErrors.nickName = !(modelControl.status === "VALID");
                break;

        }

        this.validateFormAndEmit();
    }

    /** Create new card payment */
    createCard(payerId: number, addressData: AddressFormData, cardData: CardDetailAddData): Promise<Def.CardPaymentMethodUpdate> {

        const billingAddress = {
            line1: addressData.address1,
            line2: addressData.address2,
            city: addressData.city,
            state: addressData.state,
            country: addressData.country,
            unit: addressData.unit,
            zipcode: addressData.zip
        }

        return this.cardNumberEditor.createCard({
            billingAddress: billingAddress,
            cardHolderName: cardData.nameOnCard,
            expirationMonth: cardData.expMonth,
            expirationYear:cardData.expYear,
            securityCode: cardData.secCode,
            customerId: payerId,
        }).catch (error => {
            return error as Def.CardPaymentMethodUpdate
        })
    }
}

